import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const fallbackImages = [
  "https://images.unsplash.com/photo-1758798219572-512a03a60ce0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1646662521253-5b9253b1a207?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1759745125627-333e78bc1edc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1774535140090-8f860e44dd0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1774750264848-d47a68d82122?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1765574782103-6d326d2240f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

export default function Projects() {
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/public/projects").then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
  }, []);

  const items = (projects.length ? projects : []).slice(0, 6);

  return (
    <section id="projects" className="bg-muted/40" data-testid="projects-section">
      <div className="container-page section-y">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div>
            <span className="overline">{t.projects.eyebrow}</span>
            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1]">
              {t.projects.heading1} <br /> <span className="text-primary">{t.projects.heading1Accent}</span>
            </h2>
          </div>
          <p className="max-w-md text-muted-foreground">{t.projects.description}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-5" data-testid="projects-grid">
          {items.length === 0 &&
            fallbackImages.slice(0, 6).map((img, i) => (
              <ProjectTile key={i} index={i} image={img} placeholder t={t} />
            ))}

          {items.map((p, i) => (
            <ProjectTile
              key={p.id || i}
              index={i}
              image={fallbackImages[i % fallbackImages.length]}
              project={p}
              t={t}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProjectTile({ image, project, placeholder, index, t }) {
  const spans = [
    "md:col-span-4 lg:col-span-7",
    "md:col-span-2 lg:col-span-5",
    "md:col-span-3 lg:col-span-4",
    "md:col-span-3 lg:col-span-4",
    "md:col-span-3 lg:col-span-4",
    "md:col-span-3 lg:col-span-6",
  ];
  const heights = ["h-80", "h-80", "h-72", "h-72", "h-72", "h-72"];
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.06 }}
      className={`relative overflow-hidden border border-border group ${spans[index % spans.length]} ${heights[index % heights.length]}`}
      data-testid={`project-tile-${index}`}
    >
      <img
        src={image}
        alt={project?.name || "Project"}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      <div className="absolute inset-0 p-6 flex flex-col justify-end">
        {placeholder ? (
          <>
            <div className="text-xs uppercase tracking-[0.2em] text-primary">{t.projects.featured}</div>
            <div className="mt-2 text-white text-lg font-semibold">{t.projects.loading}</div>
          </>
        ) : (
          <>
            <div className="text-xs uppercase tracking-[0.22em] text-primary">{project.service_type}</div>
            <div className="mt-2 text-white text-xl sm:text-2xl font-bold leading-tight">{project.name}</div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-white/70 text-xs">
              <span>{project.client}</span>
              <span className="h-1 w-1 rounded-full bg-white/40" />
              <span>{project.location}</span>
              <span className="h-1 w-1 rounded-full bg-white/40" />
              <span className="capitalize">{String(project.status).replace("_", " ")}</span>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
